import { ReactEditor } from 'slate-react';

/**
 * Find a DOM block element by its offset-key.
 * @param {HTMLElement} outerEl - the editor's outer DOM ref
 * @param {string}      blockKey
 */
export function getBlockElement(outerEl, blockKey) {
	if (!outerEl) {
		console.error("no ref, cannot locate");
	}

	const blocks = outerEl.querySelectorAll("[data-block]");
	for (let i = 0; i < blocks.length; i++) {
		const block = blocks.item(i);
		let thisKey = block.dataset.offsetKey;
		thisKey = thisKey.substring(0, thisKey.length - 4);
		if (thisKey === blockKey) {
			return block;
		}
	}
	return null;
}

/**
 * Record where a page-break entity lives inside a Slate block.
 * Mutates theBreak.at and theBreak.offset.
 *
 * @param {object} editor
 * @param {object} block      - Slate node
 * @param {object} theBreak   - mutable break descriptor
 * @param {number} offset     - character offset within the block
 * @param {number} blockChild - optional child index (for e.g. list-items)
 */
export function createPageBreakEntity(editor, block, theBreak, offset, blockChild) {
	const blockAt = ReactEditor.findPath(editor, block);
	theBreak.at = blockChild !== undefined ? [...blockAt, blockChild] : blockAt;
	theBreak.offset = offset;
	return true;
}

/**
 * Remove a page-break entity from the Slate content tree.
 *
 * @param {object} editor
 * @param {Array}  content  - editor.children / this.state.content
 * @param {object} theBreak - { pageBreakId }
 */
export function removePageBreakEntity(editor, content, theBreak) {
	function walkAndRemove(nodes, parentPath) {
		for (let i = 0; i < nodes.length; i++) {
			const node = nodes[i];
			const path = [...parentPath, i];
			if (node.pageBreaks && node.pageBreaks.length > 0) {
				for (const pb of node.pageBreaks) {
					if (pb.id === theBreak.pageBreakId) {
						editor.removePageBreak(theBreak, path);
					}
				}
			}
			if (node.children && Array.isArray(node.children)) {
				walkAndRemove(node.children, path);
			}
		}
	}
	walkAndRemove(content, []);
}

// ---------------------------------------------------------------------------
// Private helper — find the start-of-line character offset for a given pos
// ---------------------------------------------------------------------------

function startOfLine(pos, range, textNode) {
	const y = range.getBoundingClientRect().y;

	let end = pos;
	let start = Math.max(end - 100, 0);
	if (start === 0) return 0;
	if (start > textNode.length) start = textNode.length - 1;
	if (end > textNode.length) end = textNode.length;

	range.setStart(textNode, start);
	range.setEnd(textNode, end);

	let iterations = 0;
	const MAX_ITER = 100;

	while (range.getBoundingClientRect().y === y && iterations < MAX_ITER) {
		if (start === 0) break;
		end = start;
		start = Math.max(end - 100, 0);
		iterations++;
	}
	if (iterations === MAX_ITER || start === 0) {
		let newEnd = pos;
		let newStart = Math.max(newEnd - 100, 0);
		const mid = Math.floor((newStart + newEnd) / 2);
		end = mid;
	}

	while (start < end) {
		const mid = Math.floor((start + end) / 2);
		range.setStart(textNode, mid);
		range.setEnd(textNode, end);
		if (range.getBoundingClientRect().y === y) {
			end = mid;
		} else {
			if (start === mid) {
				return end;
			}
			start = mid;
		}
	}
	return start;
}

/**
 * Binary-search a single leaf for the character offset where a page-break falls.
 * Mutates theBreak.pos / theBreak.at if found. Returns true on success.
 *
 * @param {object} editor
 * @param {HTMLElement} content      - a [data-slate-leaf] element
 * @param {number}      pos          - target y position (px, relative to editor top)
 * @param {number}      offset       - y of the first editor block (px, absolute)
 * @param {object}      theBreak
 * @param {object}      slateNode    - Slate node for the leaf's block
 * @param {number}      blockPosition - character offset of this leaf within the block
 */
export function snapPageBreakInsideText(editor, content, pos, offset, theBreak, slateNode, blockPosition) {
	const text = content.innerText;
	let textNode = content.firstChild.firstChild;
	while (textNode.nodeType === 1) {
		textNode = textNode.firstChild;
	}
	let start = 0;
	let end = text.length;

	const range = document.createRange();
	let iterations = 0;
	while (end - start > 1) {
		iterations++;
		const mid = Math.floor((start + end) / 2);
		range.setStart(textNode, mid);
		range.setEnd(textNode, mid);
		const wordStart = mid;
		const wordEnd = mid;
		const wordBounds = range.getBoundingClientRect();
		const top = wordBounds.y - offset;
		if (top === pos || top + wordBounds.height === pos) {
			createPageBreakEntity(editor, slateNode, theBreak, startOfLine(wordStart, range, textNode) + blockPosition);
			return true;
		} else if (top + wordBounds.height < pos) {
			start = wordEnd + 1;
		} else if (top > pos) {
			end = wordStart - 1;
		} else if (top < pos && top + wordBounds.height > pos) {
			theBreak.pos = top / 1.3333;
			createPageBreakEntity(editor, slateNode, theBreak, startOfLine(wordStart, range, textNode) + blockPosition);
			return true;
		}
	}
	// Binary search converged without an exact match. This happens when the
	// target pos falls in the inter-line gap (line-height > 1 creates dead
	// space between the character ink bottom and the next line top that no
	// character rect spans). If we're within one line-height of the target,
	// snap to the start of the next line.
	const safeIdx = Math.max(0, Math.min(start, textNode.length - 1));
	range.setStart(textNode, safeIdx);
	range.setEnd(textNode, safeIdx);
	const nearBounds = range.getBoundingClientRect();
	const nearBottom = (nearBounds.y - offset) + nearBounds.height;
	const lineHeight = nearBounds.height * 1.5; // generous tolerance
	if (Math.abs(nearBottom - pos) < lineHeight) {
		// Close enough — find the start of the next line after this position
		const nextLineStart = safeIdx + 1 < textNode.length ? safeIdx + 1 : safeIdx;
		range.setStart(textNode, nextLineStart);
		range.setEnd(textNode, nextLineStart);
		const nextBounds = range.getBoundingClientRect();
		theBreak.pos = (nextBounds.y - offset) / 1.3333;
		createPageBreakEntity(editor, slateNode, theBreak, startOfLine(nextLineStart, range, textNode) + blockPosition);
		return true;
	}
	return false;
}

/**
 * Walk all [data-slate-leaf] children of a DOM node, find the one containing
 * the page-break position, and snap inside it.
 *
 * @param {object}      editor
 * @param {number}      offset  - y of the first editor block (px, absolute)
 * @param {number}      pos     - target y (px, relative to editor top)
 * @param {HTMLElement} domNode
 * @param {object}      theBreak
 */
export function snapInsideTextWithMultipleLeaves(editor, offset, pos, domNode, theBreak) {
	const slateNode = ReactEditor.toSlateNode(editor, domNode);
	const content = domNode.querySelectorAll("[data-slate-leaf=true]");
	// Line-height gap tolerance: leaf getBoundingClientRect() returns ink
	// bounds which exclude the extra spacing from line-height > 1. The
	// block element includes this spacing, so the target pos can fall in
	// the gap between the leaf ink bottom and the block bottom. We add
	// tolerance to the leaf bottom check to catch this.
	const LINE_HEIGHT_TOLERANCE = 10; // px — covers line-height up to ~1.7
	let blockPosition = 0;
	for (let cont of content) {
		const contentBounds = cont.getBoundingClientRect();
		const leafTop = contentBounds.y - offset;
		const leafBot = (contentBounds.y + contentBounds.height) - offset;
		if (leafTop < pos && (leafBot + LINE_HEIGHT_TOLERANCE) > pos) {
			// Clamp pos to within the leaf's actual ink bounds for the binary search
			const clampedPos = Math.min(pos, leafBot - 0.5);
			if (snapPageBreakInsideText(editor, cont, clampedPos, offset, theBreak, slateNode, blockPosition)) {
				return true;
			}
		}
		blockPosition += cont.innerText.length;
	}
	return false;
}

/**
 * Snap a page-break descriptor to the nearest Slate block boundary.
 * Mutates theBreak.pos / theBreak.at / theBreak.offset.
 *
 * @param {object}      editor
 * @param {Array}       content  - editor.children (used by removePageBreakEntity calls upstream)
 * @param {HTMLElement} outerEl  - the editor's outer DOM ref (divRef.current)
 * @param {object}      theBreak
 * @param {boolean}     forceNextBlock
 * @param {boolean}     manual
 */
export function snapPageBreak(editor, content, outerEl, theBreak, forceNextBlock, manual) {
	if (!outerEl) {
		console.error("no ref, cannot locate");
	}

	const inputPos = theBreak.pos;
	const pos = theBreak.pos * 1.3333;  // pts → pixels
	const blocks = outerEl.querySelectorAll("[data-slate-editor=true] > [data-slate-node=element]");
	const offset = blocks.item(0).getBoundingClientRect().y;

	for (let j = 0; j < blocks.length; j++) {
		const block = blocks.item(j);
		const bounds = block.getBoundingClientRect();
		if ((bounds.y + bounds.height) - offset > pos) {
			// In or above this block
			if (forceNextBlock) {
				const next = blocks.item(j + 1);
				if (next && next.getBoundingClientRect().height > 900) {
					theBreak.pos = (bounds.y - offset) / 1.3333;
					continue;
				}
				theBreak.pos = ((bounds.y + bounds.height) - offset) / 1.3333;
				break;
			}

			let leafContent = block.querySelectorAll("[data-slate-leaf=true]");
			const slateNode = ReactEditor.toSlateNode(editor, block);
			if (bounds.y - offset > pos) {
				// Falls between blocks — snap to gap above this block.
				// Use previous block's bottom edge to sit in the gap, not on top of this block.
				const prev = j > 0 ? blocks.item(j - 1) : null;
				theBreak.pos = prev
					? (prev.getBoundingClientRect().y + prev.getBoundingClientRect().height - offset) / 1.3333
					: (bounds.y - offset) / 1.3333;
				createPageBreakEntity(editor, slateNode, theBreak, 0);
				break;
			}
			if (block.nodeName === "DIV") {
				// Atomic block — position break in the gap above, not on the block itself.
				// Use previous block's bottom edge when available, otherwise use this block's top.
				const prevBlock = j > 0 ? blocks.item(j - 1) : null;
				const prevBottom = prevBlock
					? (prevBlock.getBoundingClientRect().y + prevBlock.getBoundingClientRect().height - offset) / 1.3333
					: (bounds.y - offset) / 1.3333;
				theBreak.pos = prevBottom;
				if (block.className === "toc-wrapper-outer") {
					const lis = block.querySelectorAll(".toc-label-container");

					for (let liIterator = 0; liIterator < lis.length; liIterator++) {
						const li = lis.item(liIterator);
						const liBounds = li.getBoundingClientRect();

						if ((liBounds.y + liBounds.height) - offset > pos) {
							if (liBounds.y - offset > pos) {
								// Break falls above this label — snap to gap above it
								const prevLi = liIterator > 0 ? lis.item(liIterator - 1) : null;
								theBreak.pos = prevLi
									? (prevLi.getBoundingClientRect().y + prevLi.getBoundingClientRect().height - offset) / 1.3333
									: (liBounds.y - offset) / 1.3333;
								const tocLabelSlateNode = ReactEditor.toSlateNode(editor, li);
								createPageBreakEntity(editor, tocLabelSlateNode, theBreak, 0, liIterator);
								return;
							} else {
								if (snapInsideTextWithMultipleLeaves(editor, offset, pos, li, theBreak)) {
									return;
								}
								// Text snap failed inside this label — snap to its top
								// rather than skipping to the next label
								theBreak.pos = (liBounds.y - offset) / 1.3333;
								const tocLabelSlateNode = ReactEditor.toSlateNode(editor, li);
								createPageBreakEntity(editor, tocLabelSlateNode, theBreak, 0, liIterator);
								return;
							}
						}
					}
					console.error("Didn't find place in an li. dropping through");
					continue;

				} else {
					if (!createPageBreakEntity(editor, slateNode, theBreak, 0)) {
						continue;
					} else {
						break;
					}
				}
			} else {
				if (block.nodeName === "UL" || block.nodeName === "OL") {
					const lis = block.querySelectorAll("[data-slate-node=element]");
					for (let liIterator = 0; liIterator < lis.length; liIterator++) {
						const li = lis.item(liIterator);
						const liBounds = li.getBoundingClientRect();
						if ((liBounds.y + liBounds.height) - offset > pos) {
							if (liBounds.y - offset > pos) {
								theBreak.pos = (liBounds.y - offset) / 1.3333;
								const liSlateNode = ReactEditor.toSlateNode(editor, li);
								createPageBreakEntity(editor, liSlateNode, theBreak, 0);
								return;
							} else {
								if (snapInsideTextWithMultipleLeaves(editor, offset, pos, li, theBreak)) {
									return;
								}
							}
						}
					}
					console.error("Didn't find place in an li. dropping through");
					continue;
				}
				if (leafContent.length === 0 || leafContent.item(0).nodeName === "BR") {
					// Empty/BR-only block — snap to its top (valid block boundary)
					theBreak.pos = (bounds.y - offset) / 1.3333;
					createPageBreakEntity(editor, slateNode, theBreak, 0);
					break;
				}
				if (snapInsideTextWithMultipleLeaves(editor, offset, pos, block, theBreak)) {
					return;
				}
				// Text snap failed — snap to block top so pos stays consistent with .at
				console.warn(`[snapPageBreak] text snap FAILED for block[${j}] ${block.nodeName}, type=${slateNode?.type}, pos=${(pos).toFixed(1)}px/${(pos/1.3333).toFixed(1)}pt, blockTop=${(bounds.y - offset).toFixed(1)}px, blockBot=${(bounds.y + bounds.height - offset).toFixed(1)}px → snapping to block top with offset=0`);
				theBreak.pos = (bounds.y - offset) / 1.3333;
				createPageBreakEntity(editor, slateNode, theBreak, 0);
				break;
			}
		}
	}
	// Diagnostic: log snap decisions
	{
		const snapBlock = theBreak.at ? blocks.item(theBreak.at[0]) : null;
		const snapBlockInfo = snapBlock
			? `dom=${snapBlock.nodeName}.${snapBlock.className||''}, top=${((snapBlock.getBoundingClientRect().y - offset) / 1.3333).toFixed(1)}pt, h=${(snapBlock.getBoundingClientRect().height / 1.3333).toFixed(1)}pt`
			: 'no-block';
		const delta = theBreak.pos - inputPos;
		console.log(`[snap] pos ${inputPos.toFixed(1)}pt → ${theBreak.pos.toFixed(1)}pt (Δ${delta.toFixed(1)}), at=[${theBreak.at}], force=${!!forceNextBlock}, ${snapBlockInfo}`);
		// Log neighbours for context on first break
		if (inputPos < 800 && theBreak.at) {
			const idx = theBreak.at[0];
			for (let k = Math.max(0, idx - 2); k <= Math.min(blocks.length - 1, idx + 2); k++) {
				const b = blocks.item(k);
				const r = b.getBoundingClientRect();
				const slateN = ReactEditor.toSlateNode(editor, b);
				console.log(`  block[${k}] ${b.nodeName}.${b.className||''} type=${slateN?.type||'?'} top=${((r.y - offset) / 1.3333).toFixed(1)}pt h=${(r.height / 1.3333).toFixed(1)}pt`);
			}
		}
	}
	return true;
}
